/**
 * Redux DevTools keeps a copy of the whole state (and every action payload) for each action.
 * With GeoJSON, paginated lists and base64 images in the store those copies get huge and can
 * crash the browser tab, so the extension is given a slimmed view instead.
 * Only what DevTools displays changes — the app's real state is never touched.
 */

const MAX_ARRAY_ITEMS = 50;
const MAX_STRING_LENGTH = 2000;
// Deep enough for the permissions tree; size is bounded by the array/string limits above
const MAX_DEPTH = 12;

const isPlainObject = (value) =>
  value !== null &&
  typeof value === "object" &&
  (Object.getPrototypeOf(value) === Object.prototype || Object.getPrototypeOf(value) === null);

export const slimForDevTools = (value, depth = 0) => {
  if (typeof value === "string") {
    return value.length > MAX_STRING_LENGTH
      ? `<<${value.length} characters hidden from Redux DevTools>>`
      : value;
  }
  if (Array.isArray(value)) {
    if (value.length > MAX_ARRAY_ITEMS) {
      return `<<${value.length} items hidden from Redux DevTools>>`;
    }
    if (depth >= MAX_DEPTH) return "<<nested data hidden from Redux DevTools>>";
    return value.map((item) => slimForDevTools(item, depth + 1));
  }
  if (isPlainObject(value)) {
    if (depth >= MAX_DEPTH) return "<<nested data hidden from Redux DevTools>>";
    const slim = {};
    Object.keys(value).forEach((key) => {
      slim[key] = slimForDevTools(value[key], depth + 1);
    });
    return slim;
  }
  return value;
};

export const REDUX_DEVTOOLS_OPTIONS = {
  // Default is 50 full-state snapshots
  maxAge: 25,
  stateSanitizer: (state) => slimForDevTools(state),
  actionSanitizer: (action) =>
    action && action.payload !== undefined
      ? { ...action, payload: slimForDevTools(action.payload) }
      : action,
};
